/**
 * VendorDetailDrawer.jsx - Part 3 Implementation
 * 
 * Checklist Items Covered:
 * - G1: Slide-in Animation
 * - G2: Header with Status Chip
 * - G3: Payment Progress Bar
 * - G4: Payment Milestones
 * - G5: Schedule Mismatch Banner
 * - G6: Contact & Schedule Section
 * - G7: Additional Contacts (multi-contact)
 * - G8: Payment History
 * - G9: Documents Section
 * - G10: Inline Name Edit
 * - G11: Log Payment Button
 * - G12: Review Payment Button
 * - G13: "Fully Paid" Badge
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import {
  X, User, Phone, Mail, Calendar, MapPin, Globe, FileText,
  DollarSign, CreditCard, CheckCircle, AlertTriangle, Plus,
  Edit2, Trash2, Download, ExternalLink, Clock, ChevronDown,
  ChevronUp, Paperclip, Upload
} from 'lucide-react';
import { theme } from '../../data/colors';
import { toast } from 'sonner';
import { MiloNotebookItems } from './_shared/MiloNotebookItems';
import { isIntelligenceNotebookEnabled } from './intelligence-notebook/intelligenceNotebookFlag';
const API_URL = process.env.REACT_APP_BACKEND_URL;
import {
  COLORS,
  formatMoney,
  formatShortDate,
  formatFullDate,
  formatPhone,
  daysUntil,
  getVendorPaid,
  getVendorContract,
  getVendorRemaining,
  getLastPayment,
  getPaymentCount,
  getDotSignal,
  getRiskLevel,
  getRiskLabel,
  getScheduleMismatch,
  getVendorExperienceGroup,
} from '../../lib/vendorInsights';

// ─── Slide-in Animation Keyframes (G1) ─────────────────────────────────────────

const slideKeyframes = `
@keyframes vendorDrawerSlideIn {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}
@keyframes vendorDrawerSlideOut {
  from { transform: translateX(0); opacity: 1; }
  to { transform: translateX(100%); opacity: 0; }
}
@keyframes vendorDrawerFadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}
`;

// Inject keyframes
if (typeof document !== 'undefined') {
  const styleId = 'vendor-drawer-keyframes';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = slideKeyframes;
    document.head.appendChild(style);
  }
}

// ─── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  // G1: Backdrop & Container
  // #4/#5 fix: drawer is portaled to document.body (escaping the <main>
  // stacking context) and lifted ABOVE the fixed admin header bar (zIndex 1000)
  // and the page footer, so neither the "Ask Milo Anything" toolbar nor the
  // footer can overlap the drawer when it pops out or the page scrolls.
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.4)',
    zIndex: 2000,
    animation: 'vendorDrawerFadeIn 0.2s ease',
  },
  drawer: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    width: 520,
    maxWidth: '100vw',
    background: theme.bgCard,
    boxShadow: '-20px 0 60px rgba(0,0,0,0.15)',
    display: 'flex',
    flexDirection: 'column',
    animation: 'vendorDrawerSlideIn 0.3s ease',
    zIndex: 2001,
  },

  // G2: Header
  header: {
    padding: '20px 24px',
    borderBottom: `1px solid ${theme.borderLight}`,
    background: theme.bgTabRail,
  },
  headerTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 14,
  },
  avatar: {
    width: 52,
    height: 52,
    borderRadius: 14,
    background: theme.accentTintLight,
    border: `2px solid ${theme.borderMedium}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
    fontWeight: 700,
    color: theme.textMuted,
  },
  headerInfo: {
    flex: 1,
  },
  vendorName: {
    fontSize: 20,
    fontWeight: 700,
    color: theme.textPrimary,
    letterSpacing: '-0.3px',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  editNameBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
    borderRadius: 6,
    opacity: 0.6,
    transition: 'opacity 0.15s',
  },
  vendorCategory: {
    fontSize: 12,
    color: theme.textMuted,
    marginTop: 2,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 8,
    borderRadius: 10,
    transition: 'background 0.15s',
  },
  statusChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    borderRadius: 999,
    border: '1px solid',
    marginTop: 8,
  },
  statusDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  statusText: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.6px',
    textTransform: 'uppercase',
  },

  // G3: Payment Progress Bar
  progressSection: {
    padding: '16px 24px',
    borderBottom: `1px solid ${theme.borderLight}`,
    background: theme.bgInput,
  },
  progressHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 10,
  },
  progressLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: theme.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
  },
  progressPct: {
    fontSize: 14,
    fontWeight: 700,
    color: theme.textPrimary,
  },
  progressTrack: {
    height: 10,
    background: theme.borderLight,
    borderRadius: 999,
    overflow: 'hidden',
    marginBottom: 8,
  },
  progressFill: {
    height: '100%',
    borderRadius: 999,
    transition: 'width 0.4s ease',
  },
  progressValues: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 11,
  },
  progressPaid: {
    color: theme.statusSuccess,
    fontWeight: 600,
  },
  progressRemaining: {
    color: theme.statusWarning,
    fontWeight: 600,
  },

  // G5: Schedule Mismatch Banner
  mismatchBanner: {
    margin: '0 24px 16px',
    padding: '12px 14px',
    borderRadius: 12,
    background: theme.statusWarningTint,
    border: `1px solid ${theme.borderMedium}`,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
  },
  mismatchText: {
    fontSize: 12,
    color: '#92400e',
    lineHeight: '18px',
  },

  // Scrollable content
  scrollContent: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: '16px 0',
  },

  // Section styling
  section: {
    padding: '0 24px',
    marginBottom: 20,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    cursor: 'pointer',
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 600,
    color: theme.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.9px',
  },
  sectionChevron: {
    transition: 'transform 0.2s',
  },

  // G4: Payment Milestones
  milestoneRail: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '8px 0',
  },
  milestoneStep: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: 80,
    flex: 1,
  },
  milestoneDot: {
    width: 20,
    height: 20,
    borderRadius: 10,
    border: `3px solid ${theme.borderLight}`,
    background: theme.bgCard,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  milestoneDotDone: {
    background: theme.statusSuccess,
    borderColor: theme.statusSuccess,
  },
  milestoneDotNext: {
    borderColor: theme.accentPrimary,
  },
  milestoneLine: {
    flex: 1,
    height: 3,
    background: theme.borderLight,
    marginTop: 8.5,
    marginLeft: -8,
    marginRight: -8,
  },
  milestoneLineDone: {
    background: theme.statusSuccess,
  },
  milestoneText: {
    fontSize: 10,
    color: theme.textMuted,
    marginTop: 8,
    textAlign: 'center',
    fontWeight: 500,
  },
  milestoneAmt: {
    fontSize: 12,
    fontWeight: 700,
    marginTop: 2,
    textAlign: 'center',
  },
  milestoneAmtGreen: { color: theme.statusSuccess },
  milestoneAmtGold: { color: theme.accentPrimary },
  milestoneAmtGrey: { color: theme.textMuted },

  // G6: Contact & Schedule
  contactGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 10,
  },
  contactCard: {
    padding: '12px 14px',
    background: theme.bgInput,
    borderRadius: 12,
    border: `1px solid ${theme.borderLight}`,
  },
  contactLabel: {
    fontSize: 9,
    color: theme.textMuted,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 4,
  },
  contactValue: {
    fontSize: 13,
    color: theme.textPrimary,
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  contactLink: {
    color: theme.accentPrimary,
    textDecoration: 'none',
  },

  // G7: Additional Contacts
  additionalContact: {
    padding: '10px 12px',
    background: theme.bgTabRail,
    borderRadius: 10,
    border: `1px solid ${theme.borderLight}`,
    marginTop: 10,
  },
  additionalContactHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  additionalContactName: {
    fontSize: 13,
    fontWeight: 600,
    color: theme.textPrimary,
  },
  additionalContactRole: {
    fontSize: 11,
    color: theme.textMuted,
  },
  addContactBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 12px',
    background: 'transparent',
    border: `1px dashed ${theme.borderMedium}`,
    borderRadius: 10,
    cursor: 'pointer',
    fontSize: 11,
    color: theme.textMuted,
    fontWeight: 600,
    marginTop: 10,
    width: '100%',
    justifyContent: 'center',
    transition: 'all 0.15s',
  },

  // G8: Payment History
  paymentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  paymentItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    background: theme.bgInput,
    borderRadius: 10,
    border: `1px solid ${theme.borderLight}`,
  },
  paymentLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  paymentIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: theme.statusSuccessTint,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  paymentDetails: {},
  paymentAmount: {
    fontSize: 14,
    fontWeight: 700,
    color: theme.statusSuccess,
  },
  paymentDate: {
    fontSize: 11,
    color: theme.textMuted,
  },
  paymentMethod: {
    fontSize: 11,
    color: theme.textMuted,
    textAlign: 'right',
  },
  emptyPayments: {
    padding: '20px',
    textAlign: 'center',
    color: theme.textMuted,
    fontSize: 12,
  },

  // G9: Documents
  documentList: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  documentItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '10px 12px',
    background: theme.bgInput,
    borderRadius: 10,
    border: `1px solid ${theme.borderLight}`,
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  documentLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  documentIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    background: theme.accentTintLight,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  documentName: {
    fontSize: 13,
    fontWeight: 500,
    color: theme.textPrimary,
  },
  documentMeta: {
    fontSize: 11,
    color: theme.textMuted,
  },
  uploadBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '10px 14px',
    background: 'transparent',
    border: `1px dashed ${theme.borderMedium}`,
    borderRadius: 10,
    cursor: 'pointer',
    fontSize: 11,
    color: theme.textMuted,
    fontWeight: 600,
    marginTop: 10,
    width: '100%',
    justifyContent: 'center',
  },

  // G13: Fully Paid Badge
  paidBadge: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '12px 20px',
    background: theme.statusSuccessTint,
    border: `1px solid rgba(47,122,87,0.22)`,
    borderRadius: 14,
    margin: '0 24px 16px',
  },
  paidBadgeText: {
    fontSize: 13,
    fontWeight: 700,
    color: theme.statusSuccess,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
  },

  // Footer Actions (G11, G12)
  footer: {
    padding: '16px 24px',
    borderTop: `1px solid ${theme.borderLight}`,
    background: theme.bgTabRail,
  },
  footerActions: {
    display: 'flex',
    gap: 10,
  },
  actionBtn: {
    flex: 1,
    minHeight: 44,
    borderRadius: 12,
    padding: '12px 16px',
    border: '1px solid',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 12,
    letterSpacing: '0.1px',
    transition: 'all 0.15s ease',
  },
  actionBtnPrimary: {
    background: theme.accentPrimary,
    borderColor: 'rgba(255,255,255,0.26)',
    color: theme.textInverse,
  },
  actionBtnSecondary: {
    background: theme.bgInput,
    borderColor: theme.borderMedium,
    color: theme.textSecondary,
  },

  // Inline Name Edit (G10)
  inlineEditInput: {
    fontSize: 20,
    fontWeight: 700,
    color: theme.textPrimary,
    border: `1px solid ${theme.borderMedium}`,
    borderRadius: 8,
    padding: '4px 8px',
    background: theme.bgCard,
    width: '100%',
    maxWidth: 280,
  },
  // L1: Inline field edit styles
  inlineFieldInput: {
    fontSize: 13,
    color: theme.textPrimary,
    border: `1px solid ${theme.borderMedium}`,
    borderRadius: 6,
    padding: '4px 8px',
    background: theme.bgCard,
    width: '100%',
    maxWidth: 180,
  },
  editableValue: {
    cursor: 'pointer',
    padding: '2px 4px',
    borderRadius: 4,
    transition: 'background 0.15s',
  },
  notesTextarea: {
    width: '100%',
    minHeight: 80,
    fontSize: 13,
    color: theme.textPrimary,
    border: `1px solid ${theme.borderMedium}`,
    borderRadius: 8,
    padding: '10px 12px',
    background: theme.bgCard,
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  notesDisplay: {
    fontSize: 13,
    color: theme.textSecondary,
    lineHeight: '20px',
    cursor: 'pointer',
    padding: '8px 12px',
    borderRadius: 8,
    background: theme.bgInput,
    border: `1px dashed ${theme.borderMedium}`,
    minHeight: 60,
  },
};

// ─── Main Component ────────────────────────────────────────────────────────────

// Vendor lifecycle statuses (mirror of VendorsTab VENDOR_STATUSES) — host-editable.
const VENDOR_STATUS_OPTIONS = ["Considering", "Shortlisted", "Hired", "InProgress", "Completed", "Canceled"];

const VendorDetailDrawer = ({
  vendor,
  eventDate,
  onClose,
  onSave,
  // #3: explicit "Save now" — flushes the debounced buffer immediately and
  // resolves once the PATCH lands. Wired to the footer Save button so the host
  // gets an instant, real confirmation instead of waiting out the 5s auto-save.
  onSaveNow,
  onLogPayment,
  onReviewPayment,
  // #92: parent signals which fields were *actually persisted* by the debounced
  // PATCH. Shape: { fields: string[], at: number }. The drawer flashes "Saved"
  // ONLY for these (no more false confirmation on edits that get coalesced away).
  savedSignal = null,
}) => {
  // State
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(vendor?.company_name || '');
  const fileInputRef = useRef(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [pendingAttachType, setPendingAttachType] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    milestones: true,
    contact: true,
    payments: true,
    documents: true,
  });
  const [additionalContacts, setAdditionalContacts] = useState(vendor?.additional_contacts || []);
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', role: '', phone: '', email: '' });

  // L1: Editable field states for auto-save coverage
  const [editingField, setEditingField] = useState(null);
  // #92: transient per-field "Saved" confirmation — now driven by the PARENT's
  // persisted-PATCH signal, NOT optimistically on edit. This is the fix for the
  // false "Saved": when several fields are edited inside one debounce window the
  // parent coalesces them into a single write and reports back exactly which
  // fields landed; we flash "Saved" only for those. `savedFields` is a Set so a
  // batched multi-field write lights every persisted field at once.
  const [savedFields, setSavedFields] = useState(() => new Set());
  const savedTimerRef = useRef(null);
  // #3: in-flight + just-confirmed state for the explicit footer Save button.
  const [savingNow, setSavingNow] = useState(false);
  const [savedAll, setSavedAll] = useState(false);
  const savedAllTimerRef = useRef(null);
  // Watch the parent's persisted signal. Each new signal (unique `at` stamp)
  // replaces the chip set and restarts the auto-dismiss timer.
  useEffect(() => {
    if (!savedSignal || !Array.isArray(savedSignal.fields) || savedSignal.fields.length === 0) return;
    // #3: the explicit Save button reports a `['__all__']` sentinel — light the
    // global "Saved" pill by the footer button rather than a per-field chip.
    if (savedSignal.fields.includes('__all__')) {
      setSavedAll(true);
      if (savedAllTimerRef.current) clearTimeout(savedAllTimerRef.current);
      savedAllTimerRef.current = setTimeout(() => setSavedAll(false), 2200);
      return;
    }
    setSavedFields(new Set(savedSignal.fields));
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    savedTimerRef.current = setTimeout(() => setSavedFields(new Set()), 2200);
  }, [savedSignal?.at]); // eslint-disable-line react-hooks/exhaustive-deps
  // Clear the timers on unmount
  useEffect(() => () => {
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);
    if (savedAllTimerRef.current) clearTimeout(savedAllTimerRef.current);
  }, []);
  // Helper kept for readability at call sites.
  const isSaved = (field) => savedFields.has(field);
  // Small reusable "Saved" chip
  const SavedChip = () => (
    <span
      data-testid="vendor-field-saved-chip"
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        fontSize: 10, fontWeight: 700, letterSpacing: '0.3px',
        color: theme.statusSuccess, marginLeft: 6,
        animation: 'vendorDrawerFadeIn 0.2s ease',
      }}
    >
      <CheckCircle size={11} color={theme.statusSuccess} /> Saved
    </span>
  );

  const [editedValues, setEditedValues] = useState({
    contact_name: vendor?.contact_name || '',
    phone: vendor?.phone || '',
    email: vendor?.email || '',
    website: vendor?.website || '',
    address: vendor?.address || '',
    balance_due_date: vendor?.balance_due_date || vendor?.due_date || '',
    notes: vendor?.notes || '',
  });

  // Sync editedValues when vendor changes
  useEffect(() => {
    setEditedValues({
      contact_name: vendor?.contact_name || '',
      phone: vendor?.phone || '',
      email: vendor?.email || '',
      website: vendor?.website || '',
      address: vendor?.address || '',
      balance_due_date: vendor?.balance_due_date || vendor?.due_date || '',
      notes: vendor?.notes || '',
    });
  }, [vendor]);

  // #92: re-sync the rendered Additional Contacts list whenever the vendor prop
  // changes (e.g. after the debounced save round-trips, or a fresh refetch lands
  // the persisted `additional_contacts`). Re-attach a stable client-only `id`
  // for React keys + removal. Guarded so we don't clobber an in-flight optimistic
  // list with an empty array while the backend field is still propagating.
  useEffect(() => {
    const incoming = vendor?.additional_contacts;
    if (Array.isArray(incoming) && incoming.length > 0) {
      setAdditionalContacts(incoming.map((c, i) => ({
        id: c.id ?? `${i}-${c.name || ''}-${c.email || ''}`,
        name: c.name || '',
        role: c.role || '',
        phone: c.phone || '',
        email: c.email || '',
      })));
    }
  }, [vendor?.additional_contacts]); // eslint-disable-line react-hooks/exhaustive-deps

  // Computed values
  const contract = getVendorContract(vendor);
  const paid = getVendorPaid(vendor);
  const remaining = getVendorRemaining(vendor);
  const pct = contract > 0 ? Math.round((paid / contract) * 100) : 0;
  const isFullyPaid = remaining === 0 && contract > 0;
  const dotSignal = getDotSignal(vendor);
  const riskLabel = getRiskLabel(vendor);
  const payments = vendor?.payments || [];
  // Surface files from BOTH stores: in-drawer uploads land in `attachments`, while a
  // contract saved on apply may land in `documents`. Merge + de-dupe (by id, else url,
  // else filename) so a saved contract always shows and neither store hides the other.
  const documents = useMemo(() => {
    const merged = [...(vendor?.documents || []), ...(vendor?.attachments || [])];
    const seen = new Set();
    return merged.filter((d) => {
      if (!d) return false;
      const sig = d.id || d.url || d.filename || d.name;
      if (sig && seen.has(sig)) return false;
      if (sig) seen.add(sig);
      return true;
    });
  }, [vendor?.documents, vendor?.attachments]);
  
  // G5: Schedule mismatch
  const mismatches = useMemo(() => {
    return getScheduleMismatch(vendor, eventDate);
  }, [vendor, eventDate]);

  // Milestone calculations
  const deposit = vendor?.deposit_amount || Math.round(contract * 0.5);
  const depositPaid = paid >= deposit;
  const fullyPaidMilestone = paid >= contract;

  // Status chip styling
  let statusLabel = vendor?.vendor_status || 'Pending';
  let statusColor = '#9ca3af';
  let statusBg = 'rgba(107,114,128,0.15)';

  if (isFullyPaid) {
    statusLabel = 'PAID IN FULL';
    statusColor = '#4A8C5A';
    statusBg = 'rgba(74,140,90,0.15)';
  } else if (dotSignal.color === COLORS.error) {
    statusLabel = dotSignal.blink ? 'OVERDUE' : 'URGENT';
    statusColor = '#dc2626';
    statusBg = 'rgba(220,38,38,0.15)';
  } else if (statusLabel === 'Hired') {
    statusColor = '#22c55e';
    statusBg = 'rgba(34,197,94,0.15)';
  }

  // Handlers
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleNameSave = () => {
    if (editedName.trim() && editedName !== vendor?.company_name) {
      // #92: report the changed field so the parent can (a) accumulate it across
      // the debounce window and (b) flash "Saved" only once it actually persists.
      onSave?.({ ...vendor, company_name: editedName.trim() }, ['company_name']);
    }
    setIsEditingName(false);
  };

  // #92 — Additional Contacts persistence.
  // CONTRACT: vendor.additional_contacts = [{ name, role, phone, email }].
  // We keep a client-only `id` in local state for stable React keys + removal,
  // but the persisted payload is normalized to the contract shape (id stripped)
  // so it round-trips cleanly through the backend `additional_contacts` field.
  const toContactPayload = (list) =>
    (list || []).map(({ name = '', role = '', phone = '', email = '' }) => ({
      name: (name || '').trim(),
      role: (role || '').trim(),
      phone: (phone || '').trim(),
      email: (email || '').trim(),
    }));

  const handleAddContact = () => {
    if (newContact.name.trim()) {
      const updated = [...additionalContacts, { ...newContact, id: Date.now() }];
      setAdditionalContacts(updated); // optimistic render of the saved list
      onSave?.({ ...vendor, additional_contacts: toContactPayload(updated) }, ['additional_contacts']);
      setNewContact({ name: '', role: '', phone: '', email: '' });
      setShowAddContact(false);
    }
  };

  const handleRemoveContact = (contactId) => {
    const updated = additionalContacts.filter(c => c.id !== contactId);
    setAdditionalContacts(updated);
    onSave?.({ ...vendor, additional_contacts: toContactPayload(updated) }, ['additional_contacts']);
  };

  // L1: Handler for inline field edit with auto-save
  const handleFieldChange = (field, value) => {
    setEditedValues(prev => ({ ...prev, [field]: value }));
  };

  const handleFieldSave = (field) => {
    const value = editedValues[field];
    const originalValue = field === 'balance_due_date' 
      ? (vendor?.balance_due_date || vendor?.due_date || '')
      : (vendor?.[field] || '');
    
    if (value !== originalValue) {
      // Build update object - handle due_date field mapping
      const updateData = { ...vendor };
      if (field === 'balance_due_date') {
        updateData.balance_due_date = value;
        updateData.due_date = value; // Keep both in sync
      } else {
        updateData[field] = value;
      }
      // #92: report which field changed; the parent accumulates it onto the
      // latest pending state and flashes "Saved" only after it truly persists.
      onSave?.(updateData, [field]);
    }
    setEditingField(null);
  };

  const handleFieldKeyDown = (e, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleFieldSave(field);
    } else if (e.key === 'Escape') {
      // Revert to original value
      const originalValue = field === 'balance_due_date' 
        ? (vendor?.balance_due_date || vendor?.due_date || '')
        : (vendor?.[field] || '');
      setEditedValues(prev => ({ ...prev, [field]: originalValue }));
      setEditingField(null);
    }
  };

  // #3: explicit Save handler for the footer button. Commits any field still open
  // for inline edit (so its latest keystrokes are buffered onto the pending batch),
  // then asks the parent to flush the debounced buffer immediately and confirm on
  // success — an instant, real "Saved" instead of waiting out the 5s auto-save.
  const handleSaveNow = async () => {
    if (editingField) {
      handleFieldSave(editingField); // capture the in-progress field first
    }
    if (!onSaveNow) return;
    setSavingNow(true);
    try {
      await onSaveNow();
    } finally {
      setSavingNow(false);
    }
  };

  // Get initials for avatar
  const initials = (vendor?.company_name || 'V')
    .split(' ')
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  // #4/#5: render via portal to <body> so the drawer escapes the <main>
  // stacking context (position:relative) that was trapping its z-index below
  // the fixed admin header bar and the page footer.
  return ReactDOM.createPortal(
    <>
      {/* G1: Backdrop */}
      <div style={styles.backdrop} onClick={onClose} data-testid="vendor-drawer-backdrop" />
      
      {/* G1: Slide-in Drawer */}
      <div style={styles.drawer} data-testid="vendor-detail-drawer">
        
        {/* G2: Header with Status Chip */}
        <div style={styles.header}>
          <div style={styles.headerTop}>
            <div style={styles.headerLeft}>
              <div style={styles.avatar}>{initials}</div>
              <div style={styles.headerInfo}>
                {/* G10: Inline Name Edit */}
                {isEditingName ? (
                  <input
                    type="text"
                    value={editedName}
                    onChange={(e) => setEditedName(e.target.value)}
                    onBlur={handleNameSave}
                    onKeyDown={(e) => e.key === 'Enter' && handleNameSave()}
                    style={styles.inlineEditInput}
                    autoFocus
                    data-testid="vendor-name-edit-input"
                  />
                ) : (
                  <div style={styles.vendorName}>
                    {vendor?.company_name}
                    <button
                      style={styles.editNameBtn}
                      onClick={() => setIsEditingName(true)}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = 1}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = 0.6}
                      data-testid="vendor-name-edit-btn"
                    >
                      <Edit2 size={14} color={theme.textMuted} />
                    </button>
                    {isSaved('company_name') && <SavedChip />}
                  </div>
                )}
                <div style={styles.vendorCategory}>
                  {vendor?.role_category} · {getVendorExperienceGroup(vendor?.role_category)}
                </div>
                
                {/* Status Chip — derived at-a-glance state (PAID / OVERDUE / lifecycle) */}
                <div
                  style={{
                    ...styles.statusChip,
                    background: statusBg,
                    borderColor: statusBg.replace('0.15', '0.3'),
                  }}
                >
                  <div style={{ ...styles.statusDot, background: statusColor }} />
                  <span style={{ ...styles.statusText, color: statusColor }}>{statusLabel}</span>
                </div>
                {/* Editable vendor status — host changes the lifecycle stage directly */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
                  <span style={{ fontSize: 10.5, fontWeight: 600, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Set status</span>
                  <select
                    value={vendor?.vendor_status || 'Considering'}
                    onChange={(e) => { onSave?.({ ...vendor, vendor_status: e.target.value }, ['vendor_status']); }}
                    data-testid="vendor-status-select"
                    style={{
                      fontSize: 12, fontWeight: 600, color: theme.textPrimary,
                      padding: '5px 10px', borderRadius: 8,
                      border: `1px solid ${theme.borderMedium}`, background: theme.bgCard, cursor: 'pointer',
                    }}
                  >
                    {VENDOR_STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  {isSaved('vendor_status') && <SavedChip />}
                </div>
              </div>
            </div>
            
            <button
              style={styles.closeBtn}
              onClick={onClose}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(198,167,90,0.15)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              data-testid="vendor-drawer-close"
            >
              <X size={20} color={theme.textMuted} />
            </button>
          </div>
        </div>

        {/* G3: Payment Progress Bar */}
        <div style={styles.progressSection}>
          <div style={styles.progressHeader}>
            <span style={styles.progressLabel}>Payment Progress</span>
            <span style={styles.progressPct}>{pct}%</span>
          </div>
          <div style={styles.progressTrack}>
            <div
              style={{
                ...styles.progressFill,
                width: `${Math.min(pct, 100)}%`,
                background: pct >= 100 ? '#4A8C5A' : pct > 50 ? '#C6A75A' : '#EAB354',
              }}
            />
          </div>
          <div style={styles.progressValues}>
            <span style={styles.progressPaid}>{formatMoney(paid)} paid</span>
            <span style={styles.progressRemaining}>{formatMoney(remaining)} remaining</span>
          </div>
        </div>

        {/* G13: Fully Paid Badge */}
        {isFullyPaid && (
          <div style={styles.paidBadge} data-testid="vendor-fully-paid-badge">
            <CheckCircle size={20} color={theme.statusSuccess} />
            <span style={styles.paidBadgeText}>Fully Paid</span>
          </div>
        )}

        {/* G5: Schedule Mismatch Banner */}
        {mismatches.length > 0 && (
          <div style={styles.mismatchBanner} data-testid="vendor-mismatch-banner">
            <AlertTriangle size={18} color={theme.statusWarning} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              {mismatches.map((m, i) => (
                <div key={i} style={styles.mismatchText}>{m.detail}</div>
              ))}
            </div>
          </div>
        )}

        {/* Scrollable Content */}
        <div style={styles.scrollContent}>
          
          {/* G4: Payment Milestones */}
          <div style={styles.section}>
            <div style={styles.sectionHeader} onClick={() => toggleSection('milestones')}>
              <span style={styles.sectionTitle}>Payment Milestones</span>
              <ChevronDown
                size={16}
                color={theme.textMuted}
                style={{
                  ...styles.sectionChevron,
                  transform: expandedSections.milestones ? 'rotate(180deg)' : 'rotate(0)',
                }}
              />
            </div>
            {expandedSections.milestones && (
              <div style={styles.milestoneRail}>
                {/* Deposit Step */}
                <div style={styles.milestoneStep}>
                  <div
                    style={{
                      ...styles.milestoneDot,
                      ...(depositPaid ? styles.milestoneDotDone : styles.milestoneDotNext),
                    }}
                  >
                    {depositPaid && <CheckCircle size={12} color={theme.textInverse} />}
                  </div>
                  <span style={styles.milestoneText}>Deposit</span>
                  <span
                    style={{
                      ...styles.milestoneAmt,
                      ...(depositPaid ? styles.milestoneAmtGreen : styles.milestoneAmtGold),
                    }}
                  >
                    {formatMoney(deposit)}
                  </span>
                </div>
                
                {/* Line */}
                <div
                  style={{
                    ...styles.milestoneLine,
                    ...(depositPaid ? styles.milestoneLineDone : {}),
                  }}
                />
                
                {/* Balance Step */}
                <div style={styles.milestoneStep}>
                  <div
                    style={{
                      ...styles.milestoneDot,
                      ...(fullyPaidMilestone ? styles.milestoneDotDone : {}),
                      ...(!fullyPaidMilestone && depositPaid ? styles.milestoneDotNext : {}),
                    }}
                  >
                    {fullyPaidMilestone && <CheckCircle size={12} color={theme.textInverse} />}
                  </div>
                  <span style={styles.milestoneText}>Balance</span>
                  <span
                    style={{
                      ...styles.milestoneAmt,
                      ...(fullyPaidMilestone ? styles.milestoneAmtGreen : depositPaid ? styles.milestoneAmtGold : styles.milestoneAmtGrey),
                    }}
                  >
                    {formatMoney(contract - deposit)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Phase 3 — Contract & Obligations (Milo watches these live) */}
          {(vendor.guest_minimum || vendor.final_count_date || vendor.cancellation_deadline) && (
            <div style={styles.section}>
              <div style={{ ...styles.sectionTitle, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                <FileText size={13} color={theme.accentPrimary} /> Milo Advisory — Contract Standouts
              </div>
              <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 8, lineHeight: 1.5, fontStyle: 'italic' }}>
                Things to be aware of in this contract — a possible issue to review, not legal advice.
              </div>
              {vendor.guest_minimum ? (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 12.5, color: theme.textSecondary, padding: '6px 0', lineHeight: 1.5 }}>
                  <User size={14} color={theme.accentPrimary} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span><b>{vendor.guest_minimum}-guest minimum</b>{vendor.final_count_date ? ` · final count due ${formatShortDate(vendor.final_count_date)}` : ''}</span>
                </div>
              ) : (vendor.final_count_date ? (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 12.5, color: theme.textSecondary, padding: '6px 0', lineHeight: 1.5 }}>
                  <Calendar size={14} color={theme.accentPrimary} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>Final guest count due <b>{formatShortDate(vendor.final_count_date)}</b></span>
                </div>
              ) : null)}
              {vendor.cancellation_deadline ? (
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 9, fontSize: 12.5, color: theme.textSecondary, padding: '6px 0', lineHeight: 1.5 }}>
                  <Clock size={14} color={theme.statusWarning} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>Penalty-free cancellation until <b>{formatShortDate(vendor.cancellation_deadline)}</b></span>
                </div>
              ) : null}
              <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 8, lineHeight: 1.5 }}>
                Milo watches these against your live RSVPs and timeline, and flags risks in Event Readiness.
              </div>
            </div>
          )}

          {/* G6: Contact & Schedule */}
          <div style={styles.section}>
            <div style={styles.sectionHeader} onClick={() => toggleSection('contact')}>
              <span style={styles.sectionTitle}>Contact & Schedule</span>
              <ChevronDown
                size={16}
                color={theme.textMuted}
                style={{
                  ...styles.sectionChevron,
                  transform: expandedSections.contact ? 'rotate(180deg)' : 'rotate(0)',
                }}
              />
            </div>
            {expandedSections.contact && (
              <>
                <div style={styles.contactGrid}>
                  {/* L1: Editable Primary Contact */}
                  <div style={styles.contactCard}>
                    <div style={styles.contactLabel}>Primary Contact{isSaved('contact_name') && <SavedChip />}</div>
                    <div style={styles.contactValue}>
                      <User size={14} color={theme.textMuted} />
                      {editingField === 'contact_name' ? (
                        <input
                          type="text"
                          value={editedValues.contact_name}
                          onChange={(e) => handleFieldChange('contact_name', e.target.value)}
                          onBlur={() => handleFieldSave('contact_name')}
                          onKeyDown={(e) => handleFieldKeyDown(e, 'contact_name')}
                          style={styles.inlineFieldInput}
                          autoFocus
                          data-testid="edit-contact-name"
                        />
                      ) : (
                        <span
                          style={styles.editableValue}
                          onClick={() => setEditingField('contact_name')}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(198,167,90,0.12)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          data-testid="contact-name-value"
                        >
                          {editedValues.contact_name || 'Add contact...'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* L1: Editable Phone */}
                  <div style={styles.contactCard}>
                    <div style={styles.contactLabel}>Phone{isSaved('phone') && <SavedChip />}</div>
                    <div style={styles.contactValue}>
                      <Phone size={14} color={theme.textMuted} />
                      {editingField === 'phone' ? (
                        <input
                          type="tel"
                          value={editedValues.phone}
                          onChange={(e) => handleFieldChange('phone', e.target.value)}
                          onBlur={() => handleFieldSave('phone')}
                          onKeyDown={(e) => handleFieldKeyDown(e, 'phone')}
                          style={styles.inlineFieldInput}
                          autoFocus
                          data-testid="edit-phone"
                        />
                      ) : (
                        <span
                          style={styles.editableValue}
                          onClick={() => setEditingField('phone')}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(198,167,90,0.12)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          data-testid="phone-value"
                        >
                          {editedValues.phone ? formatPhone(editedValues.phone) : 'Add phone...'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* L1: Editable Email */}
                  <div style={styles.contactCard}>
                    <div style={styles.contactLabel}>Email{isSaved('email') && <SavedChip />}</div>
                    <div style={styles.contactValue}>
                      <Mail size={14} color={theme.textMuted} />
                      {editingField === 'email' ? (
                        <input
                          type="email"
                          value={editedValues.email}
                          onChange={(e) => handleFieldChange('email', e.target.value)}
                          onBlur={() => handleFieldSave('email')}
                          onKeyDown={(e) => handleFieldKeyDown(e, 'email')}
                          style={styles.inlineFieldInput}
                          autoFocus
                          data-testid="edit-email"
                        />
                      ) : (
                        <span
                          style={styles.editableValue}
                          onClick={() => setEditingField('email')}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(198,167,90,0.12)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          data-testid="email-value"
                        >
                          {editedValues.email || 'Add email...'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* L1: Editable Payment Due Date */}
                  <div style={styles.contactCard}>
                    <div style={styles.contactLabel}>Payment Due{isSaved('balance_due_date') && <SavedChip />}</div>
                    <div style={styles.contactValue}>
                      <Calendar size={14} color={theme.textMuted} />
                      {editingField === 'balance_due_date' ? (
                        <input
                          type="date"
                          value={editedValues.balance_due_date ? editedValues.balance_due_date.split('T')[0] : ''}
                          onChange={(e) => handleFieldChange('balance_due_date', e.target.value)}
                          onBlur={() => handleFieldSave('balance_due_date')}
                          onKeyDown={(e) => handleFieldKeyDown(e, 'balance_due_date')}
                          style={styles.inlineFieldInput}
                          autoFocus
                          data-testid="edit-due-date"
                        />
                      ) : (
                        <span
                          style={styles.editableValue}
                          onClick={() => setEditingField('balance_due_date')}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(198,167,90,0.12)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          data-testid="due-date-value"
                        >
                          {editedValues.balance_due_date ? formatFullDate(editedValues.balance_due_date) : 'Set due date...'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* L1: Editable Website */}
                  <div style={styles.contactCard}>
                    <div style={styles.contactLabel}>Website{isSaved('website') && <SavedChip />}</div>
                    <div style={styles.contactValue}>
                      <Globe size={14} color={theme.textMuted} />
                      {editingField === 'website' ? (
                        <input
                          type="url"
                          value={editedValues.website}
                          onChange={(e) => handleFieldChange('website', e.target.value)}
                          onBlur={() => handleFieldSave('website')}
                          onKeyDown={(e) => handleFieldKeyDown(e, 'website')}
                          style={styles.inlineFieldInput}
                          placeholder="https://..."
                          autoFocus
                          data-testid="edit-website"
                        />
                      ) : (
                        <span
                          style={styles.editableValue}
                          onClick={() => setEditingField('website')}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(198,167,90,0.12)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          data-testid="website-value"
                        >
                          {editedValues.website ? editedValues.website.replace(/^https?:\/\//, '').slice(0, 25) : 'Add website...'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* L1: Editable Address */}
                  <div style={styles.contactCard}>
                    <div style={styles.contactLabel}>Address{isSaved('address') && <SavedChip />}</div>
                    <div style={styles.contactValue}>
                      <MapPin size={14} color={theme.textMuted} />
                      {editingField === 'address' ? (
                        <input
                          type="text"
                          value={editedValues.address}
                          onChange={(e) => handleFieldChange('address', e.target.value)}
                          onBlur={() => handleFieldSave('address')}
                          onKeyDown={(e) => handleFieldKeyDown(e, 'address')}
                          style={styles.inlineFieldInput}
                          autoFocus
                          data-testid="edit-address"
                        />
                      ) : (
                        <span
                          style={styles.editableValue}
                          onClick={() => setEditingField('address')}
                          onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(198,167,90,0.12)'}
                          onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                          data-testid="address-value"
                        >
                          {editedValues.address || 'Add address...'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* L1: Editable Notes Field */}
                <div style={{ marginTop: 16 }}>
                  <div style={{ ...styles.contactLabel, marginBottom: 8 }}>Notes{isSaved('notes') && <SavedChip />}</div>
                  {editingField === 'notes' ? (
                    <textarea
                      value={editedValues.notes}
                      onChange={(e) => handleFieldChange('notes', e.target.value)}
                      onBlur={() => handleFieldSave('notes')}
                      style={styles.notesTextarea}
                      placeholder="Add notes about this vendor..."
                      autoFocus
                      data-testid="edit-notes"
                    />
                  ) : (
                    <div
                      style={styles.notesDisplay}
                      onClick={() => setEditingField('notes')}
                      onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(198,167,90,0.4)'}
                      onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(198,167,90,0.22)'}
                      data-testid="notes-value"
                    >
                      {editedValues.notes || 'Click to add notes...'}
                    </div>
                  )}
                </div>

                {/* G7: Additional Contacts */}
                {additionalContacts.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ ...styles.sectionTitle, marginBottom: 8 }}>Additional Contacts</div>
                    {additionalContacts.map((contact) => (
                      <div key={contact.id} style={styles.additionalContact}>
                        <div style={styles.additionalContactHeader}>
                          <div>
                            <div style={styles.additionalContactName}>{contact.name}</div>
                            {contact.role && (
                              <div style={styles.additionalContactRole}>{contact.role}</div>
                            )}
                          </div>
                          <button
                            onClick={() => handleRemoveContact(contact.id)}
                            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                          >
                            <Trash2 size={14} color={theme.statusError} />
                          </button>
                        </div>
                        <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 12, color: '#8B8478' }}>
                          {contact.phone && <span>{formatPhone(contact.phone)}</span>}
                          {contact.email && <span>{contact.email}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add Contact Form */}
                {showAddContact ? (
                  <div style={{ ...styles.additionalContact, marginTop: 10 }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                      <input
                        type="text"
                        placeholder="Name *"
                        value={newContact.name}
                        onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                        style={{ padding: '8px 10px', border: '1px solid rgba(198,167,90,0.22)', borderRadius: 8, fontSize: 12 }}
                      />
                      <input
                        type="text"
                        placeholder="Role"
                        value={newContact.role}
                        onChange={(e) => setNewContact({ ...newContact, role: e.target.value })}
                        style={{ padding: '8px 10px', border: '1px solid rgba(198,167,90,0.22)', borderRadius: 8, fontSize: 12 }}
                      />
                      <input
                        type="tel"
                        placeholder="Phone"
                        value={newContact.phone}
                        onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                        style={{ padding: '8px 10px', border: '1px solid rgba(198,167,90,0.22)', borderRadius: 8, fontSize: 12 }}
                      />
                      <input
                        type="email"
                        placeholder="Email"
                        value={newContact.email}
                        onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                        style={{ padding: '8px 10px', border: '1px solid rgba(198,167,90,0.22)', borderRadius: 8, fontSize: 12 }}
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                      <button
                        onClick={handleAddContact}
                        style={{ ...styles.actionBtn, ...styles.actionBtnPrimary, flex: 1, minHeight: 34, fontSize: 11 }}
                      >
                        Save Contact
                      </button>
                      <button
                        onClick={() => setShowAddContact(false)}
                        style={{ ...styles.actionBtn, ...styles.actionBtnSecondary, flex: 1, minHeight: 34, fontSize: 11 }}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    style={styles.addContactBtn}
                    onClick={() => setShowAddContact(true)}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = 'rgba(198,167,90,0.5)'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(198,167,90,0.3)'}
                    data-testid="add-contact-btn"
                  >
                    <Plus size={14} /> Add Additional Contact
                  </button>
                )}
              </>
            )}
          </div>

          {/* G8: Payment History */}
          <div style={styles.section}>
            <div style={styles.sectionHeader} onClick={() => toggleSection('payments')}>
              <span style={styles.sectionTitle}>Payment History ({payments.length})</span>
              <ChevronDown
                size={16}
                color={theme.textMuted}
                style={{
                  ...styles.sectionChevron,
                  transform: expandedSections.payments ? 'rotate(180deg)' : 'rotate(0)',
                }}
              />
            </div>
            {expandedSections.payments && (
              <div style={styles.paymentList}>
                {payments.length === 0 ? (
                  <div style={styles.emptyPayments}>
                    <DollarSign size={24} color={theme.accentPrimary} style={{ marginBottom: 8 }} />
                    <div>No payments recorded yet</div>
                  </div>
                ) : (
                  payments.map((payment, i) => (
                    <div key={i} style={styles.paymentItem}>
                      <div style={styles.paymentLeft}>
                        <div style={styles.paymentIcon}>
                          <CreditCard size={16} color={theme.statusSuccess} />
                        </div>
                        <div style={styles.paymentDetails}>
                          <div style={styles.paymentAmount}>{formatMoney(payment.amount)}</div>
                          <div style={styles.paymentDate}>{formatFullDate(payment.date)}</div>
                        </div>
                      </div>
                      <div style={styles.paymentMethod}>
                        {payment.method || 'Payment'}
                        {payment.paid_by && <div>{payment.paid_by}</div>}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* G9: Documents */}
          <div style={styles.section}>
            <div style={styles.sectionHeader} onClick={() => toggleSection('documents')}>
              <span style={styles.sectionTitle}>Documents ({documents.length})</span>
              <ChevronDown
                size={16}
                color={theme.textMuted}
                style={{
                  ...styles.sectionChevron,
                  transform: expandedSections.documents ? 'rotate(180deg)' : 'rotate(0)',
                }}
              />
            </div>
            {expandedSections.documents && (
              <div style={styles.documentList}>
                <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 8, lineHeight: 1.5 }}>
                  Files on record for this vendor. To have Milo read a contract and extract its terms, use the <b>Contract Scanner</b> on the Vendors overview.
                </div>
                {documents.length === 0 ? (
                  <div style={styles.emptyPayments}>
                    <FileText size={24} color={theme.accentPrimary} style={{ marginBottom: 8 }} />
                    <div>No documents uploaded yet</div>
                  </div>
                ) : (
                  documents.map((doc, i) => {
                    const href = (doc.url || '').startsWith('http') ? doc.url : `${API_URL}${doc.url || ''}`;
                    return (
                      <div
                        key={doc.id || i}
                        data-testid={`vendor-attachment-row-${i}`}
                        style={styles.documentItem}
                        onMouseEnter={(e) => e.currentTarget.style.background = theme.statusWarningTint}
                        onMouseLeave={(e) => e.currentTarget.style.background = theme.bgCard}
                      >
                        <div style={styles.documentLeft}>
                          <div style={styles.documentIcon}>
                            <FileText size={16} color={theme.accentPrimary} />
                          </div>
                          <div>
                            <div style={styles.documentName}>{doc.filename || doc.name || `Document ${i + 1}`}</div>
                            <div style={styles.documentMeta}>
                              {(doc.type || 'document').toUpperCase()} · {doc.uploaded_at ? formatShortDate(doc.uploaded_at) : 'Uploaded'}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          {doc.url && (
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid={`vendor-attachment-view-${i}`}
                              style={{ color: theme.accentPrimary, display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, textDecoration: 'none' }}
                              title="View / download"
                            >
                              <Download size={14} /> View
                            </a>
                          )}
                          <button
                            data-testid={`vendor-attachment-delete-${i}`}
                            onClick={async () => {
                              if (!doc.id) return;
                              if (!window.confirm(`Delete ${doc.filename || 'this attachment'}?`)) return;
                              try {
                                const r = await fetch(`${API_URL}/api/vendors/${vendor.id || vendor._id}/attachments/${doc.id}`, { method: 'DELETE' });
                                if (!r.ok) throw new Error('Delete failed');
                                toast.success('Attachment removed');
                                onSave?.({ ...vendor, attachments: (vendor.attachments || []).filter(a => a.id !== doc.id) });
                              } catch { toast.error('Failed to delete attachment'); }
                            }}
                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: theme.statusDangerText, padding: 2 }}
                            title="Delete"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })
                )}

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.webp,.txt,.csv,.xlsx,.xls"
                  style={{ display: 'none' }}
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 20 * 1024 * 1024) { toast.error('File must be under 20 MB'); e.target.value = ''; return; }
                    const type = pendingAttachType || 'document';
                    setUploadingAttachment(true);
                    try {
                      const fd = new FormData();
                      fd.append('file', f);
                      const r = await fetch(`${API_URL}/api/vendors/${vendor.id || vendor._id}/attachments/upload?type=${encodeURIComponent(type)}`, {
                        method: 'POST', body: fd,
                      });
                      const d = await r.json();
                      if (!r.ok) throw new Error(d.detail || 'Upload failed');
                      toast.success(`${d.filename} uploaded`);
                      onSave?.({ ...vendor, attachments: [ ...(vendor.attachments || []), d ] });
                    } catch (err) { toast.error(err.message || 'Upload failed'); }
                    finally {
                      setUploadingAttachment(false);
                      setPendingAttachType(null);
                      if (e.target) e.target.value = '';
                    }
                  }}
                  data-testid="vendor-attachment-file-input"
                />
                <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                  <button
                    style={{ ...styles.uploadBtn, flex: 1 }}
                    onClick={() => { setPendingAttachType('contract'); fileInputRef.current?.click(); }}
                    disabled={uploadingAttachment}
                    data-testid="upload-contract-drawer-btn"
                  >
                    <Upload size={14} /> {uploadingAttachment && pendingAttachType === 'contract' ? 'Uploading…' : 'Upload Contract'}
                  </button>
                  <button
                    style={{ ...styles.uploadBtn, flex: 1 }}
                    onClick={() => { setPendingAttachType('document'); fileInputRef.current?.click(); }}
                    disabled={uploadingAttachment}
                    data-testid="upload-document-btn"
                  >
                    <Upload size={14} /> {uploadingAttachment && pendingAttachType === 'document' ? 'Uploading…' : 'Upload Document'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Milo Intelligence Notebook — read-only, flag-gated (default OFF) */}
          {isIntelligenceNotebookEnabled() && (
            <div style={{ marginTop: 16 }}>
              <MiloNotebookItems kind="vendor_followup" title="Milo Follow-ups" />
            </div>
          )}
        </div>

        {/* G11, G12: Footer Actions */}
        <div style={styles.footer}>
          {/* #3: Explicit Save — persists the drawer's edits immediately and
              confirms once the PATCH lands, so the host never has to trust the
              silent 5s auto-save. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <button
              style={{
                ...styles.actionBtn,
                ...styles.actionBtnPrimary,
                flex: 1,
                opacity: savingNow ? 0.7 : 1,
                cursor: savingNow ? 'default' : 'pointer',
              }}
              onClick={handleSaveNow}
              disabled={savingNow}
              data-testid="vendor-drawer-save-btn"
            >
              <CheckCircle size={16} />
              {savingNow ? 'Saving…' : 'Save'}
            </button>
            {savedAll && (
              <span
                data-testid="vendor-drawer-saved-all"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  fontSize: 12, fontWeight: 700, color: theme.statusSuccess,
                  animation: 'vendorDrawerFadeIn 0.2s ease', whiteSpace: 'nowrap',
                }}
              >
                <CheckCircle size={14} color={theme.statusSuccess} /> Saved
              </span>
            )}
          </div>
          <div style={styles.footerActions}>
            {isFullyPaid ? (
              <button
                style={{ ...styles.actionBtn, ...styles.actionBtnSecondary, flex: 1 }}
                onClick={() => onReviewPayment?.(vendor)}
                data-testid="review-payment-btn"
              >
                <Clock size={16} />
                Review Payments
              </button>
            ) : (
              <>
                {/* G11: Log Payment Button */}
                <button
                  style={{ ...styles.actionBtn, ...styles.actionBtnPrimary, flex: 1 }}
                  onClick={() => onLogPayment?.(vendor)}
                  data-testid="log-payment-btn"
                >
                  <CreditCard size={16} />
                  Log Payment
                </button>
                
                {/* G12: Review Payment Button */}
                <button
                  style={{ ...styles.actionBtn, ...styles.actionBtnSecondary, flex: 1 }}
                  onClick={() => onReviewPayment?.(vendor)}
                  data-testid="review-payment-btn"
                >
                  <Clock size={16} />
                  Review
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default VendorDetailDrawer;
